import React, { useEffect, useMemo } from 'react';
import { withStyles } from '@material-ui/core/styles';
import DataGrid from 'devextreme-react/data-grid';
import { Tooltip } from 'devextreme-react/tooltip';
import { dataGridExtension } from './index';
import { compose } from 'recompose';
import { connect } from "react-redux";
import { setGridStateById } from 'store/actions';
import { useDialogFocusContext } from 'components/layout/DialogWithFocusProvider';
import Popup, { Properties } from "devextreme/ui/popup";

const styles = () => ({
  gridRoot: {
    maxWidth: '100%!important'
  },
});

export function cleanState(state, fixedColumns) { // Pass in fixedColumns to override state with field config for fixed columns
  const newState = {
    ...state,
    columns: state.columns.map(col => {
      const newCol = { 
        ...col,
        fixed: fixedColumns ? fixedColumns.includes(col.dataField) : col.fixed
      };
      if (col.filterValue) {
        newCol.filterValue = cleanNestedArrays(col.filterValue);
      }
      return newCol;
    }),
    allowedPageSizes: state.allowedPageSizes?.length ? [...state.allowedPageSizes] : state.allowedPageSizes,
    filterPanel: { ...state.filterPanel },
    filterValue: cleanNestedArrays(state.filterValue),
    selectedRowKeys: state.selectedRowKeys?.length ? [...state.selectedRowKeys] : state.selectedRowKeys
  }
  return newState;
}

function cleanNestedArrays(arr) {
  if (!Array.isArray(arr)) return arr;
  const newArray = [];
  arr.forEach(el => {
    if (Array.isArray(el)) {
      newArray.push(cleanNestedArrays(el));
    } else {
      newArray.push(el);
    }
  });
  return newArray;
}

function Grid({ classes, id, appSettings, gridState, setGridStateById, remoteOperations, persistState, enableTooltips, tooltips,
  ...rest }, ref) { // Custom props: enableTooltips, persistState
  const setDisableEnforceFocus = useDialogFocusContext();
  if (setDisableEnforceFocus) {
    Popup.defaultOptions({
      device: { deviceType: "desktop" },
      options: {
        onContentReady(e) {
          if (e.element.classList.contains("dx-datagrid-column-chooser")) {
            e.component.option("position.at", "right");
          }
          if (e.element.classList.contains('dx-header-filter-menu')) {
            setDisableEnforceFocus(true);
          }
        },
        onHidden(e) {
          if (e.element.classList.contains('dx-header-filter-menu')) {
            setDisableEnforceFocus(false);
          }
        },
      },
    });
  }

  useEffect(() => {
    if (!id || !ref?.current) return;
    const gridRef = ref.current;
    if (gridState) {
      gridRef.instance.state(cleanState(gridState));
    }
    return function() {
      if (persistState) {
        const state = gridRef.instance.state();
        if (Object.keys(state).length === 0) return;
        setGridStateById(id, state);
      }
    }
  }, []);

  let tooltipComponents = useMemo(() => {
    if (!enableTooltips || (!id && !tooltips)) return null;
    const columns = tooltips || appSettings.FieldSettings.filter(field => field[id]);
    return columns.map(({
      field,
      displayName,
      tooltip
    }) => {
      const target = `div#${id} td[aria-label='Column ${displayName}']`;
      return (
        <Tooltip
          key={field}
          target={target}
          showEvent="dxhoverstart"
          hideEvent="dxhoverend"
          position="top"
        >
          <p>{tooltip ?? displayName}</p>
        </Tooltip>
      )
    })
  }, [enableTooltips, appSettings.FieldSettings, id]);

  const elementAttr = { class: classes.gridRoot };
  return (
    <>
      {remoteOperations
        ? <DataGrid
            id={id}
            ref={ref}
            allowColumnResizing
            showBorders
            showRowLines
            showColumnLines
            width="100%"
            columnResizingMode="widget"
            onCellClick={dataGridExtension.defaultSortingClick}
            elementAttr={elementAttr}
            remoteOperations={remoteOperations}
            { ...rest }
          />
        : <DataGrid
            id={id}
            ref={ref}
            showBorders
            onCellClick={dataGridExtension.defaultSortingClick}
            { ...rest }
          />
      }
      {tooltipComponents}
    </>
  );
}

const mapState = (state, ownProps) => {
  return {
    gridState: ownProps.id ? state.appState.gridState[ownProps.id] : null,
    appSettings: state.appState.appSettings
  }
};

const mapDispatch = {
  setGridStateById
}

export default compose(
  withStyles(styles),
  connect(mapState, mapDispatch, null, { forwardRef: true })
  )(React.forwardRef(Grid));
